import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Alert,
  CircularProgress,
  Autocomplete,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { hu } from 'date-fns/locale';
import { authService } from '../services/authService';
import { idoraforditasService, WorkLocationType, WORK_LOCATION_LABELS, WORK_TYPES_BY_LOCATION } from '../services/idoraforditasService';
import { apiService, getApiErrorMessage } from '../services/apiService';

interface IdoraforditasData {
  datum: Date | null;
  bejegyzesTargya: string;
  idoraforditasErteke: number;
  workLocation: WorkLocationType;
  workType: string;
}

interface AddIdoraforditasDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  munkaszam?: string | null;
}

interface MunkaszamOption {
  id: number;
  munkaszam: string;
  munkaTargya?: string;
}

const normalizeMunkaszamValue = (value: string): string => {
  const cleaned = (value || '').trim().replace(/\s+/g, '').toLowerCase();
  const match = cleaned.match(/^(\d{2,4})-(\d{1,3})\/(\d{1,})$/);

  if (!match) {
    return cleaned;
  }

  const year = Number(match[1]) % 100;
  const sorszam = Number(match[2]);
  const suffixRaw = match[3];
  const suffix = suffixRaw.length > 4 ? suffixRaw : Number(suffixRaw).toString().padStart(4, '0');

  return `${year.toString().padStart(2, '0')}-${sorszam.toString().padStart(3, '0')}/${suffix}`;
};

const buildMunkaszamValue = (item: any): string => {
  if (item?.munkaszam) {
    return normalizeMunkaszamValue(String(item.munkaszam));
  }

  if (item?.ev && item?.sorszam && item?.id) {
    const year = Number(item.ev) % 100;
    const sorszam = Number(item.sorszam);
    const id = Number(item.id);
    return `${year.toString().padStart(2, '0')}-${sorszam.toString().padStart(3, '0')}/${id.toString().padStart(4, '0')}`;
  }

  return '';
};

const AddIdoraforditasDialog: React.FC<AddIdoraforditasDialogProps> = ({
  open,
  onClose,
  onSuccess,
  munkaszam,
}) => {
  const [formData, setFormData] = useState<IdoraforditasData>({
    datum: new Date(),
    bejegyzesTargya: '',
    idoraforditasErteke: 0,
    workLocation: 'office',
    workType: '',
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loadingMunkaszamok, setLoadingMunkaszamok] = useState(false);
  const [munkaszamok, setMunkaszamok] = useState<MunkaszamOption[]>([]);
  const [selectedMunkaszam, setSelectedMunkaszam] = useState<MunkaszamOption | null>(null);

  const normalizeMunkaszam = (value: string): string => normalizeMunkaszamValue(value);

  React.useEffect(() => {
    const loadMunkaszamok = async () => {
      if (!open) {
        return;
      }

      setLoadingMunkaszamok(true);

      try {
        const list = await apiService.getData('munkaszamok');
        const mapped: MunkaszamOption[] = list
          .filter((item: any) => item?.id)
          .map((item: any) => ({
            id: item.id,
            munkaszam: buildMunkaszamValue(item),
            munkaTargya: item.munkaTargya,
          }))
          .filter((item: MunkaszamOption) => !!item.munkaszam);

        setMunkaszamok(mapped);
      } catch (error) {
        setMunkaszamok([]);
        setSelectedMunkaszam(null);
        setError(getApiErrorMessage(error, 'Nem sikerült a munkaszámok betöltése.'));
      } finally {
        setLoadingMunkaszamok(false);
      }
    };

    loadMunkaszamok();
  }, [open, munkaszam]);

  React.useEffect(() => {
    if (!open || !munkaszam) {
      return;
    }

    const target = normalizeMunkaszam(munkaszam);
    const match = munkaszamok.find((item) => normalizeMunkaszam(item.munkaszam) === target);

    if (match) {
      setSelectedMunkaszam(match);
      return;
    }

    setSelectedMunkaszam({
      id: 0,
      munkaszam: normalizeMunkaszamValue(munkaszam),
      munkaTargya: '',
    });
  }, [open, munkaszam, munkaszamok]);

  const handleChange = (field: keyof IdoraforditasData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError(null);
  };

  const handleSubmit = async () => {
    // Validáció
    if (!formData.datum) {
      setError('Kérjük, adja meg a dátumot');
      return;
    }

    if (!formData.bejegyzesTargya.trim()) {
      setError('Kérjük, adja meg a bejegyzés tárgyát');
      return;
    }

    if (formData.idoraforditasErteke <= 0) {
      setError('Az időráfordításnak nagyobbnak kell lennie 0-nál');
      return;
    }

    if (!selectedMunkaszam) {
      setError('Kérjük, válasszon munkaszámot');
      return;
    }

    const user = authService.getCurrentUser();
    if (!user || !user.userId) {
      setError('Nem található felhasználói azonosító');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const toLocalDateTimeString = (date: Date): string => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}T12:00:00`;
      };

      await idoraforditasService.createIdoraforditas({
        datum: toLocalDateTimeString(formData.datum),
        bejegyzesTargya: formData.bejegyzesTargya,
        idoraforditasErteke: formData.idoraforditasErteke,
        munkaszamErteke: selectedMunkaszam.munkaszam,
        userId: user.userId,
        munkaszamId: selectedMunkaszam.id,
        workLocation: formData.workLocation,
        workType: formData.workType || undefined,
      });

      // Sikeres létrehozás
      setFormData({
        datum: new Date(),
        bejegyzesTargya: '',
        idoraforditasErteke: 0,
        workLocation: 'office',
        workType: '',
      });
      if (!munkaszam) {
        setSelectedMunkaszam(null);
      }
      onSuccess();
      onClose();
    } catch (err: any) {
      console.error('Error creating idoraforditas:', err);
      setError(getApiErrorMessage(err, 'Hiba történt az időráfordítás létrehozása során.'));
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setFormData({
        datum: new Date(),
        bejegyzesTargya: '',
        idoraforditasErteke: 0,
        workLocation: 'office',
        workType: '',
      });
      if (!munkaszam) {
        setSelectedMunkaszam(null);
      }
      setError(null);
      onClose();
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>Új időráfordítás hozzáadása</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
          {error && (
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          <Autocomplete
            options={munkaszamok}
            loading={loadingMunkaszamok}
            value={selectedMunkaszam}
            disabled={!!munkaszam}
            onChange={(_, newValue) => {
              setSelectedMunkaszam(newValue);
              setError(null);
            }}
            getOptionLabel={(option) => option.munkaszam}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderOption={(props, option) => (
              <Box component="li" {...props}>
                {option.munkaszam} {option.munkaTargya ? `- ${option.munkaTargya}` : ''}
              </Box>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Munkaszám *"
                required
                helperText={munkaszam
                  ? "A munkaszám ebben a nézetben rögzített"
                  : "Válassza ki, melyik munkaszámhoz rögzíti az időt"}
              />
            )}
          />

          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={hu}>
            <DatePicker
              label="Dátum *"
              value={formData.datum}
              onChange={(newValue) => handleChange('datum', newValue)}
              slotProps={{
                textField: {
                  fullWidth: true,
                  required: true,
                },
              }}
            />
          </LocalizationProvider>

          <TextField
            label="Bejegyzés tárgya"
            value={formData.bejegyzesTargya}
            onChange={(e) => handleChange('bejegyzesTargya', e.target.value)}
            fullWidth
            required
            multiline
            rows={3}
            placeholder="Mi volt a munkavégzés tárgya?"
          />

          <TextField
            label="Időráfordítás (óra)"
            type="number"
            value={formData.idoraforditasErteke || ''}
            onChange={(e) => handleChange('idoraforditasErteke', parseFloat(e.target.value) || 0)}
            fullWidth
            required
            inputProps={{
              min: 0,
              step: 0.5,
            }}
            placeholder="Pl.: 2.5"
          />

          <FormControl fullWidth required>
            <InputLabel>Munkavégzés helyszíne</InputLabel>
            <Select
              value={formData.workLocation}
              label="Munkavégzés helyszíne"
              onChange={(e) => {
                const newLocation = e.target.value as WorkLocationType;
                handleChange('workLocation', newLocation);
                // Töröljük a munka típusát, ha helyszínt váltunk
                handleChange('workType', '');
              }}
            >
              <MenuItem value="office">{WORK_LOCATION_LABELS.office}</MenuItem>
              <MenuItem value="home_office">{WORK_LOCATION_LABELS.home_office}</MenuItem>
              <MenuItem value="offsite">{WORK_LOCATION_LABELS.offsite}</MenuItem>
            </Select>
            <FormHelperText>
              Válassza ki, hogy hol végezte a munkát
            </FormHelperText>
          </FormControl>

          <Autocomplete
            freeSolo
            options={WORK_TYPES_BY_LOCATION[formData.workLocation]}
            value={formData.workType}
            onChange={(_, newValue) => handleChange('workType', newValue || '')}
            onInputChange={(_, newValue) => handleChange('workType', newValue)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Munka típusa"
                placeholder="Válasszon a listából vagy írjon be egyedi típust"
                helperText="Opcionális - segít a részletesebb elemzésben"
              />
            )}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Mégse
        </Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained" 
          disabled={loading || loadingMunkaszamok}
        >
          {loading ? <CircularProgress size={24} /> : 'Hozzáadás'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddIdoraforditasDialog;
